import api from './apiClient';

// Base dashboard stats interface - common marker for role-specific stats
export interface BaseDashboardStats {}

// Role-specific stats interfaces
export interface SuperAdminStats extends BaseDashboardStats {
  totalCompanies: number;
  totalUsers: number;
  activeSubscriptions: number;
  totalRevenue: number;
  systemUptime: number;
  apiRequests: number;
  newCompaniesThisMonth: number;
  activeUsersToday: number;
  systemHealth: {
    database: string;
    apiServer: string;
    storageUsage: number;
    memoryUsage: number;
  };
}

export interface SafetyStaffStats extends BaseDashboardStats {
  activeContractors: number;
  pendingPermits: number;
  expiringDocuments: number;
  completedCourses: number;
  pendingApprovals: number;
  complianceRate: number;
  riskAssessments: {
    high: number;
    medium: number;
    low: number;
  };
  incidentsThisMonth: number;
  inspectionsScheduled: number;
}

export interface CompanyAdminStats extends BaseDashboardStats {
  totalContractors: number;
  activePermits: number;
  complianceRate: number;
  pendingApprovals: number;
  monthlyGrowth: number;
  contractorsByStatus: {
    active: number;
    inactive: number;
    suspended: number;
  };
  departmentStats: {
    [department: string]: number;
  };
  trainingCompletion: number;
}

export interface ClientApproverStats extends BaseDashboardStats {
  pendingApprovals: number;
  approvedToday: number;
  rejectedToday: number;
  avgResponseTime: number;
  approvalsByType: {
    critical_task_permit: number;
    access_request: number;
    document_validation: number;
  };
  departmentDistribution: {
    [department: string]: number;
  };
}

export interface ClientStaffStats extends BaseDashboardStats {
  completedCourses: number;
  activeCourses: number;
  certificatesEarned: number;
  overallProgress: number;
  nextCertificationExpiry: string;
  hoursCompleted: number;
  complianceStatus: string;
  upcomingDeadlines: number;
}

export interface SecurityStats extends BaseDashboardStats {
  pendingVerifications: number;
  accessesGranted: number;
  incidentsReported: number;
  toolsRegistered: number;
  todayStats: {
    entrances: number;
    exits: number;
    currentInside: number;
    violations: number;
  };
  vehicleAccess: {
    pending: number;
    approved: number;
    inPremises: number;
  };
}

export interface ContratistaAdminStats extends BaseDashboardStats {
  teamMembers: number;
  activePermits: number;
  completedTrainings: number;
  pendingTasks: number;
  companyCompliance: number;
  teamStats: {
    active: number;
    onLeave: number;
    suspended: number;
  };
  trainingStats: {
    completed: number;
    inProgress: number;
    overdue: number;
  };
  documentStats: {
    valid: number;
    expiring: number;
    expired: number;
  };
}

export interface ContratistaSubalternosStats extends BaseDashboardStats {
  assignedTasks: number;
  completedTasks: number;
  activeCourses: number;
  complianceScore: number;
  currentProject: {
    id: string;
    name: string;
    supervisor: string;
  };
  monthlyStats: {
    hoursWorked: number;
    tasksCompleted: number;
    safetyIncidents: number;
  };
}

// Legacy interface for backward compatibility
export interface DashboardStats {
  totalTeamMembers: number;
  activePermits: number;
  completedTrainings: number;
  pendingTasks: number;
  complianceScore: number;
  activeContractors: number;
  expiredCertifications: number;
  upcomingDeadlines: number;
}

// Response types for role-specific endpoints
export interface SystemHealthData {
  database: string;
  apiServer: string;
  storageUsage: number;
  memoryUsage: number;
  uptime: number;
}

export interface RevenueData {
  totalRevenue: number;
  monthlyRevenue: number;
  growth: number;
  byPlan: Record<string, number>;
}

export interface ApiUsageData {
  totalRequests: number;
  requestsToday: number;
  averageResponseTime: number;
  errorRate: number;
}

export interface ServiceManagementData {
  success: boolean;
  data: {
    serviceCards: Record<string, unknown>;
    criticalTaskPermits: Record<string, unknown>;
  };
}

export interface RiskAssessmentData {
  assessments: Array<{ id: string; level: string; area: string; date: string }>;
  summary: { high: number; medium: number; low: number };
}

export interface IncidentData {
  incidents: Array<{ id: string; type: string; severity: string; date: string; status: string }>;
  total: number;
}

export interface InspectionData {
  inspections: Array<{ id: string; area: string; date: string; status: string }>;
  scheduled: number;
}

export interface ContractorsResponse {
  contractors: Array<Record<string, unknown>>;
  total: number;
  page: number;
  limit: number;
}

export interface MyCoursesResponse {
  courses: Array<Record<string, unknown>>;
  training?: Array<Record<string, unknown>>;
}

export interface MyTasksResponse {
  tasks: Array<Record<string, unknown>>;
}

export interface TodayStatsData {
  entrances: number;
  exits: number;
  currentInside: number;
  violations: number;
}

export interface VehicleAccessData {
  pending: number;
  approved: number;
  inPremises: number;
  vehicles: Array<Record<string, unknown>>;
}

export interface TeamResponse {
  members: Array<Record<string, unknown>>;
  total: number;
}

export interface ProjectsResponse {
  projects: Array<Record<string, unknown>>;
  total: number;
}

export interface InvoicesResponse {
  invoices: Array<Record<string, unknown>>;
  total: number;
}

export interface DashboardActivity {
  id: string;
  type: string;
  description: string;
  timestamp: string;
  user: {
    name: string;
    role: string;
  };
  status: string;
  priority?: string;
}

export interface DashboardAlert {
  id: string;
  type: 'warning' | 'error' | 'info' | 'success';
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  priority: 'high' | 'medium' | 'low';
  actionRequired: boolean;
}

class DashboardService {
  private static instance: DashboardService;

  private constructor() {}

  static getInstance(): DashboardService {
    if (!DashboardService.instance) {
      DashboardService.instance = new DashboardService();
    }
    return DashboardService.instance;
  }

  async getStats<T extends BaseDashboardStats>(): Promise<T> {
    const data = await api.get('/dashboard/stats');
    return data as T;
  }

  async getActivities(limit: number = 10, offset: number = 0): Promise<DashboardActivity[]> {
    const data = await api.get('/dashboard/activities', { limit, offset });
    return data.activities || data;
  }

  async getAlerts(type: string = 'all'): Promise<DashboardAlert[]> {
    const data = await api.get('/dashboard/alerts', { type });
    return data.alerts || data;
  }

  async markAlertAsRead(alertId: string): Promise<void> {
    try {
      await api.put(`/dashboard/alerts/${alertId}/read`);
    } catch (error: unknown) {
    }
  }

  // Role-specific API endpoints
  async getSystemHealth(): Promise<SystemHealthData | null> {
    try {
      return await api.get('/dashboard/system-health');
    } catch (error: unknown) {
      return null;
    }
  }

  async getRevenue(): Promise<RevenueData | null> {
    try {
      return await api.get('/dashboard/revenue');
    } catch (error: unknown) {
      return null;
    }
  }

  async getApiUsage(): Promise<ApiUsageData | null> {
    try {
      return await api.get('/dashboard/api-usage');
    } catch (error: unknown) {
      return null;
    }
  }

  async getServiceManagementStats(): Promise<ServiceManagementData> {
    try {
      const [scData, ctpData] = await Promise.all([
        api.get('/service-cards/stats').catch(() => ({ success: false, data: {} })),
        api.get('/critical-task-permits/stats').catch(() => ({ success: false, data: {} })),
      ]);

      return {
        success: true,
        data: {
          serviceCards: scData.data || {},
          criticalTaskPermits: ctpData.data || {},
        },
      };
    } catch (error: unknown) {
      return {
        success: false,
        data: {
          serviceCards: {},
          criticalTaskPermits: {},
        },
      };
    }
  }

  async getRiskAssessments(): Promise<RiskAssessmentData | null> {
    try {
      return await api.get('/dashboard/risk-assessments');
    } catch (error: unknown) {
      return null;
    }
  }

  async getIncidents(): Promise<IncidentData | null> {
    try {
      return await api.get('/dashboard/incidents');
    } catch (error: unknown) {
      return null;
    }
  }

  async getInspections(): Promise<InspectionData | null> {
    try {
      return await api.get('/dashboard/inspections');
    } catch (error: unknown) {
      return null;
    }
  }

  async getContractors(page: number = 1, limit: number = 20, filters?: Record<string, unknown>): Promise<ContractorsResponse | null> {
    try {
      const params: Record<string, unknown> = {
        page,
        limit,
        ...(filters || {})
      };
      return await api.get('/dashboard/contractors', params);
    } catch (error: unknown) {
      return null;
    }
  }

  async getMyCourses(): Promise<MyCoursesResponse | null> {
    try {
      return await api.get('/dashboard/my-courses');
    } catch (error: unknown) {
      return null;
    }
  }

  async getMyTasks(): Promise<MyTasksResponse | null> {
    try {
      return await api.get('/dashboard/my-tasks');
    } catch (error: unknown) {
      return null;
    }
  }

  async getTodayStats(): Promise<TodayStatsData | null> {
    try {
      return await api.get('/dashboard/today-stats');
    } catch (error: unknown) {
      return null;
    }
  }

  async getVehicleAccess(): Promise<VehicleAccessData | null> {
    try {
      return await api.get('/dashboard/vehicle-access');
    } catch (error: unknown) {
      return null;
    }
  }

  async getTeam(page: number = 1, limit: number = 10): Promise<TeamResponse | null> {
    try {
      return await api.get('/dashboard/team', { page, limit });
    } catch (error: unknown) {
      return null;
    }
  }

  async getProjects(): Promise<ProjectsResponse | null> {
    try {
      return await api.get('/dashboard/projects');
    } catch (error: unknown) {
      return null;
    }
  }

  async getInvoices(): Promise<InvoicesResponse | null> {
    try {
      return await api.get('/dashboard/invoices');
    } catch (error: unknown) {
      return null;
    }
  }
}

export default DashboardService.getInstance();
